import React, { useState, useEffect, useRef } from 'react';
import decode from 'jwt-decode';
import { withApollo } from 'react-apollo';
import {
  GET_LIVE_VIDEO,
  GET_DEVICES_LIVE_DATA_VT_BY_UNIQUE_IDS,
  GET_DEVICES_CAM_COUNT_VT,
  GET_VEHICLE_DETAIL,
} from '../../graphql/queries';
import VTVideoContainer from './VTVideoContainer';
import VTCamSwapModal from '../../components/VTCamSwapModal/VTCamSwapModal';
import './VTLiveStream.css';

const DEFAULT_CAMERAS = ['Road View', 'Driver View', '3rd View', '4th View'];
const DEFAULT_CHANNEL_MAP = {
  'Road View': 1,
  'Driver View': 2,
  '3rd View': 3,
  '4th View': 4,
};

const STREAM_DURATION = 60; // 60 seconds

function VTLiveStream({ client }) {
  const [vehicleInfo, setVehicleInfo] = useState({
    vehicleNumber: 'Loading...',
    uniqueId: '',
    title: 'Video Telematics',
    exp: null,
  });

  const [availableCameras, setAvailableCameras] = useState(DEFAULT_CAMERAS);
  const [channelMap, setChannelMap] = useState(DEFAULT_CHANNEL_MAP);

  const [isExpired, setIsExpired] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedCameras, setSelectedCameras] = useState(['Road View']);
  const [cameraStates, setCameraStates] = useState({ 'Road View': 'connecting' });
  const [cameraUrls, setCameraUrls] = useState({});
  const [timers, setTimers] = useState({}); // { [cameraName]: secondsLeft }

  const [swapModalOpen, setSwapModalOpen] = useState(false);
  const [pendingCamera, setPendingCamera] = useState(null);

  const cameraMenuRef = useRef(null);
  const liveStatusIntervalRef = useRef(null);

  // Fetches live status (and vehicle number on first call) from getDevicesLiveDataVTByUniqueIds.
  // isInitial=true also resolves vehicleNumber via fallback if needed.
  const fetchLiveStatus = (apolloClient, uniqueId, isInitial = false) => {
    apolloClient
      .query({
        query: GET_DEVICES_LIVE_DATA_VT_BY_UNIQUE_IDS,
        variables: { uniqueIds: [String(uniqueId)] },
        fetchPolicy: 'network-only',
      })
      .then((res) => {
        const liveData = res?.data?.getDevicesLiveDataVTByUniqueIds?.[0];
        if (!liveData) return;

        const hasVehicleNumber = liveData.vehicleNumber && liveData.vehicleNumber !== uniqueId;

        if (hasVehicleNumber) {
          setVehicleInfo((prev) => ({
            ...prev,
            vehicleNumber: liveData.vehicleNumber,
            isLive: liveData.status === true,
          }));
        } else {
          // Always update live status
          setVehicleInfo((prev) => ({ ...prev, isLive: liveData.status === true }));

          // Only run vehicle number fallback on the initial call
          if (isInitial) {
            apolloClient
              .query({
                query: GET_VEHICLE_DETAIL,
                variables: { id: String(uniqueId) },
                fetchPolicy: 'network-only',
              })
              .then((vRes) => {
                const vData = vRes?.data?.getVehicleDetail;
                if (vData) {
                  const foundNum = vData.vehicleNumber || vData.registrationNo;
                  if (foundNum) {
                    setVehicleInfo((prev) => ({ ...prev, vehicleNumber: foundNum }));
                  }
                }
              })
              .catch((err) => console.error('Failed to fetch vehicle detail fallback:', err));
          }
        }
      })
      .catch((err) => console.error('Failed to fetch vehicle live data:', err));
  };

  // Check URL hash or JWT expiration
  useEffect(() => {
    if (window.location.hash === '#expired') {
      setIsExpired(true);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token || token === 'null') {
        setIsExpired(true);
        return;
      }

      const decoded = decode(token);
      const exp = decoded.exp;

      if (exp && exp < Math.round(new Date().getTime() / 1000)) {
        setIsExpired(true);
        return;
      }

      const targetUniqueId =
        decoded.uniqueId ||
        (Array.isArray(decoded.uniqueIds) && decoded.uniqueIds[0]) ||
        localStorage.getItem('vehicle') ||
        '';

      const initialVehNumber = decoded.vehicleNumber || null;

      setVehicleInfo({
        vehicleNumber: initialVehNumber,
        uniqueId: targetUniqueId,
        title: decoded.title || 'Live Stream',
        exp: exp ? exp * 1000 : null,
      });

      // Fetch vehicle live status and vehicle number (initial call)
      if (client && targetUniqueId) {
        fetchLiveStatus(client, targetUniqueId, true);

        // Fetch camera channels for device
        client
          .query({
            query: GET_DEVICES_CAM_COUNT_VT,
            variables: { uniqueIds: [String(targetUniqueId)] },
            fetchPolicy: 'network-only',
          })
          .then((res) => {
            const camInfo = res?.data?.getDevicesCamCountVT?.[0];
            if (camInfo) {
              const { channelDescription, channels, camCount } = camInfo;
              let cams = [];
              let newMap = {};

              if (Array.isArray(channelDescription) && channelDescription.length > 0) {
                cams = channelDescription.map((ch) => ch.label);
                channelDescription.forEach((ch) => {
                  newMap[ch.label] = ch.value;
                });
              } else if (Array.isArray(channels) && channels.length > 0) {
                cams = channels.map((ch) => `Cam ${ch}`);
                channels.forEach((ch) => {
                  newMap[`Cam ${ch}`] = ch;
                });
              } else if (camCount && camCount > 0) {
                for (let i = 1; i <= camCount; i++) {
                  const label = i === 1 ? 'Road View' : i === 2 ? 'Driver View' : `${i}rd View`;
                  cams.push(label);
                  newMap[label] = i;
                }
              }

              if (cams.length > 0) {
                setAvailableCameras(cams);
                setChannelMap(newMap);

                // If first available camera label is different from default 'Road View', fetch it
                if (cams[0] !== 'Road View') {
                  setSelectedCameras([cams[0]]);
                  setCameraStates({ [cams[0]]: 'connecting' });
                  fetchStream(cams[0]);
                }
              }
            }
          })
          .catch((err) => {
            console.error('Failed to fetch device camera count:', err);
          });
      }
    } catch (err) {
      console.error('Error decoding token in VTLiveStream:', err);
      setIsExpired(true);
    }
  }, []);

  // Poll live status every 20 seconds
  useEffect(() => {
    const uniqueId = vehicleInfo.uniqueId;
    if (!client || !uniqueId || isExpired) return;

    liveStatusIntervalRef.current = setInterval(() => {
      fetchLiveStatus(client, uniqueId);
    }, 20000);

    return () => {
      clearInterval(liveStatusIntervalRef.current);
      liveStatusIntervalRef.current = null;
    };
  }, [vehicleInfo.uniqueId, isExpired]);

  // Listen to click outside camera menu
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (cameraMenuRef.current && !cameraMenuRef.current.contains(e.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Timer interval for live streams
  useEffect(() => {
    const hasTimers = Object.keys(timers).length > 0;
    if (!hasTimers) return;

    const interval = setInterval(() => {
      setTimers((prev) => {
        const next = { ...prev };
        let updated = false;

        Object.keys(next).forEach((cam) => {
          if (typeof next[cam] === 'number' && next[cam] > 0) {
            next[cam] -= 1;
            updated = true;
            if (next[cam] === 0) {
              endStream(cam);
            }
          }
        });

        return updated ? next : prev;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [timers]);

  // Fetch initial stream
  useEffect(() => {
    if (!isExpired) {
      fetchStream('Road View');
    }
  }, [vehicleInfo.uniqueId, isExpired]);

  const fetchStream = async (cam) => {
    setCameraStates((prev) => ({ ...prev, [cam]: 'connecting' }));

    const rawCh = channelMap[cam] !== undefined ? channelMap[cam] : 1;
    const chId = Number(rawCh) || 1;
    const targetUniqueId = vehicleInfo.uniqueId || localStorage.getItem('vehicle');

    try {
      if (client && targetUniqueId) {
        const res = await client.query({
          query: GET_LIVE_VIDEO,
          variables: {
            uniqueId: String(targetUniqueId),
            channelId: chId,
            bitStreamType: 1,
          },
          fetchPolicy: 'network-only',
        });

        const liveData = res?.data?.getLiveVideo;
        const videoUrl = liveData?.url;
        const msg = liveData?.message;

        if (videoUrl) {
          setCameraUrls((prev) => ({ ...prev, [cam]: videoUrl }));
          startStream(cam);
          return;
        } else {
          // Response returned null URL (e.g. "device offline")
          setCameraStates((prev) => ({
            ...prev,
            [cam]: { type: 'error', message: msg || 'Device offline or video unavailable' },
          }));
          return;
        }
      }
    } catch (err) {
      console.error(`Failed to fetch video stream for ${cam}:`, err);
      setCameraStates((prev) => ({
        ...prev,
        [cam]: { type: 'error', message: 'Failed to connect to stream' },
      }));
    }
  };

  const startStream = (cam) => {
    setCameraStates((prev) => ({ ...prev, [cam]: 'live' }));
    setTimers((prev) => ({ ...prev, [cam]: STREAM_DURATION }));
  };

  const endStream = (cam) => {
    setCameraStates((prev) => ({ ...prev, [cam]: 'ended' }));
    setTimers((prev) => {
      const next = { ...prev };
      delete next[cam];
      return next;
    });
  };

  const refreshStream = (cam) => {
    fetchStream(cam);
  };

  const toggleCamera = (cam) => {
    if (selectedCameras.includes(cam)) {
      if (selectedCameras.length === 1) return; // keep at least 1 camera
      setSelectedCameras((prev) => prev.filter((x) => x !== cam));

      setCameraStates((prev) => {
        const next = { ...prev };
        delete next[cam];
        return next;
      });

      setTimers((prev) => {
        const next = { ...prev };
        delete next[cam];
        return next;
      });
    } else {
      // If adding a new view while views exist, prompt VTCamSwapModal
      setPendingCamera(cam);
      setSwapModalOpen(true);
      setIsMenuOpen(false);
    }
  };

  const handleAddView = (cam) => {
    setSwapModalOpen(false);
    if (!cam) return;
    setSelectedCameras((prev) => [...prev, cam]);
    fetchStream(cam);
  };

  const handleSwapView = (cam) => {
    setSwapModalOpen(false);
    if (!cam) return;

    // Replace current selected cameras with single new camera view
    setSelectedCameras([cam]);
    setCameraStates({ [cam]: 'connecting' });
    setCameraUrls({});
    setTimers({});
    fetchStream(cam);
  };

  const formatTimerLabel = (sec) => {
    const s = Math.max(0, sec || 0);
    const m = Math.floor(s / 60);
    const r = String(s % 60).padStart(2, '0');
    return `${m}:${r}`;
  };

  const getGridClass = () => {
    const len = selectedCameras.length;
    if (len === 1) return 'grid one';
    if (len === 2) return 'grid two';
    if (len === 3) return 'grid three';
    return 'grid four';
  };

  const formatExpiryTime = () => {
    if (!vehicleInfo.exp) {
      return {
        date: 'Valid until token expiration time',
      };
    }

    const expDate = new Date(vehicleInfo.exp);
    const day = expDate.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    const time = expDate.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });

    return {
      date: `Valid until ${day}, ${time}`,
    };
  };

  const expiryInfo = formatExpiryTime();

  if (isExpired) {
    return (
      <div className="vt-app-container">
        <header className="header">
          <div className="logo">
            <svg width="202" height="71" viewBox="0 0 202 71" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clipPath="url(#clip0_670_468)">
                <path d="M37.1676 57.0306C36.2477 57.2033 35.6965 57.3299 35.1335 57.4143C32.4848 57.7979 29.8402 58.4156 27.1796 58.4923C22.477 58.6304 19.7649 56.3746 19.5469 51.8707C19.4358 49.5957 19.2693 3.54004 19.2693 3.54004L3.74609 6.06821V45.2223H3.75402C3.75402 45.5215 3.75402 45.8169 3.75402 46.1161C3.77781 49.5804 3.96814 53.0792 4.53118 56.4935C5.55813 62.743 8.97601 67.293 15.3558 69.5756C20.5897 71.4477 32.4928 71.8007 39.3523 68.3748C38.595 64.4348 37.8972 60.8248 37.1676 57.0306Z" fill="#139CD6"/>
                <path d="M183.403 38.5923C180.663 26.9796 170.838 21.0754 158.376 23.5537C157.452 23.7379 156.536 23.9719 155.572 24.2212L155.521 3.55469C155.521 3.55469 147.531 4.90126 144.339 5.41533L140.43 6.0445V68.8728C140.43 68.8728 153.189 70.9675 159.117 70.9675C161.631 70.9675 164.208 70.7527 166.797 70.1925C173.387 68.7769 178.225 65.3587 181.179 60.0223C183.367 56.067 184.462 51.7856 184.462 47.0669C184.462 44.3852 184.109 41.5655 183.407 38.5846L183.403 38.5923ZM155.79 37.0654C159.327 35.6767 162.059 35.3467 164.49 36.5859C169.486 39.1294 169.145 49.6909 166.972 53.9992C165.651 56.6233 163.356 57.9353 159.958 58.0082C158.804 58.0351 157.63 57.9353 156.381 57.8317C156.187 57.8164 155.989 57.8011 155.79 57.7857V37.0654Z" fill="#139CD6"/>
                <path d="M87.1191 68.4558C76.5245 70.7307 66.2272 71.8932 55.7674 70.0862C44.1101 68.0721 38.5233 54.8174 45.5336 45.8173C48.2457 42.3377 52.1037 40.7073 56.3621 39.8019C61.7665 38.6548 67.2026 38.8121 72.6268 39.5794C73.6339 36.3184 71.5444 32.4437 68.063 31.8951C64.8474 31.3849 61.5088 31.3427 58.2416 31.4961C55.4145 31.6266 52.6152 32.3209 49.3876 32.835C48.769 29.1483 48.1505 25.4615 47.4844 21.51C49.3797 21.0573 50.9419 20.5893 52.5359 20.3169C60.2201 19.0049 67.9203 18.2107 75.561 20.5586C82.3571 22.6456 86.0723 27.3106 86.8138 34.0051C87.2975 38.3824 87.2658 42.8211 87.3174 47.2329C87.3927 53.4325 87.3451 59.6321 87.3253 65.8278C87.3253 66.6603 87.1945 67.4967 87.1151 68.4596L87.1191 68.4558ZM72.8845 49.5539C68.7014 48.4491 64.7403 48.5066 60.8546 49.7496C58.4993 50.5054 57.4287 52.3123 57.5953 54.5873C57.7816 57.1269 58.9077 58.3776 61.5841 59.0451C65.339 59.9812 69.0702 59.7894 72.8845 59.141V49.5539Z" fill="#139CD6"/>
                <path d="M97.8729 33.0265C97.2583 29.2477 96.6635 25.5955 96.0251 21.69C97.9165 21.2373 99.4787 20.7731 101.081 20.5007C108.832 19.181 116.6 18.3831 124.288 20.8077C130.906 22.8946 134.455 27.5136 135.347 34.0316C135.735 36.8782 135.878 66.8519 135.878 68.8698C131.033 69.5757 126.092 70.646 121.378 70.8494C115.882 71.0834 110.272 71.0527 104.855 70.2509C94.7484 68.7509 90.363 61.9413 91.4138 52.592C92.3099 44.6085 98.9395 41.2249 104.911 39.9934C110.319 38.877 115.751 39.0151 121.172 39.7862C122.119 36.4447 120.049 32.6352 116.564 32.0904C113.349 31.5879 110.006 31.5457 106.743 31.6991C103.916 31.8334 101.12 32.524 97.8768 33.0342L97.8729 33.0265ZM121.378 49.7493C117.183 48.6406 113.115 48.6521 109.126 50.0639C107.274 50.7199 106.112 52.0972 106.1 54.1304C106.088 55.9949 106.445 57.7596 108.42 58.6113C112.607 60.4259 116.921 59.9579 121.378 59.3748V49.7493Z" fill="#139CD6"/>
                <path d="M187.75 27.1689H200.545V70.9727H187.75V27.1689Z" fill="#139CD6"/>
                <path d="M201.995 13.6562C201.983 18.0833 198.664 21.2253 194.033 21.2023C189.513 21.1793 186.297 18.0296 186.293 13.614C186.289 9.12157 189.532 5.97574 194.164 5.98341C198.751 5.99109 202.01 9.18295 201.999 13.6562H201.995Z" fill="#F6881F"/>
                <path d="M34.5317 17.5889H0V30.4561H34.5317V17.5889Z" fill="#F6881F"/>
              </g>
              <defs>
                <clipPath id="clip0_670_468">
                  <rect width="202" height="70.973" fill="white"/>
                </clipPath>
              </defs>
            </svg>
          </div>
          <div className="context">Live Stream</div>
        </header>
        <section id="expired" className="expired show">
          <div className="expired-card">
            <div className="expired-icon">⌛</div>
            <h2>This link has expired</h2>
            <p>Please contact your fleet operator for a new link.</p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="vt-app-container">
      <header className="header">
        <div className="logo">
          <svg width="202" height="71" viewBox="0 0 202 71" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0_670_468)">
              <path d="M37.1676 57.0306C36.2477 57.2033 35.6965 57.3299 35.1335 57.4143C32.4848 57.7979 29.8402 58.4156 27.1796 58.4923C22.477 58.6304 19.7649 56.3746 19.5469 51.8707C19.4358 49.5957 19.2693 3.54004 19.2693 3.54004L3.74609 6.06821V45.2223H3.75402C3.75402 45.5215 3.75402 45.8169 3.75402 46.1161C3.77781 49.5804 3.96814 53.0792 4.53118 56.4935C5.55813 62.743 8.97601 67.293 15.3558 69.5756C20.5897 71.4477 32.4928 71.8007 39.3523 68.3748C38.595 64.4348 37.8972 60.8248 37.1676 57.0306Z" fill="#139CD6"/>
              <path d="M183.403 38.5923C180.663 26.9796 170.838 21.0754 158.376 23.5537C157.452 23.7379 156.536 23.9719 155.572 24.2212L155.521 3.55469C155.521 3.55469 147.531 4.90126 144.339 5.41533L140.43 6.0445V68.8728C140.43 68.8728 153.189 70.9675 159.117 70.9675C161.631 70.9675 164.208 70.7527 166.797 70.1925C173.387 68.7769 178.225 65.3587 181.179 60.0223C183.367 56.067 184.462 51.7856 184.462 47.0669C184.462 44.3852 184.109 41.5655 183.407 38.5846L183.403 38.5923ZM155.79 37.0654C159.327 35.6767 162.059 35.3467 164.49 36.5859C169.486 39.1294 169.145 49.6909 166.972 53.9992C165.651 56.6233 163.356 57.9353 159.958 58.0082C158.804 58.0351 157.63 57.9353 156.381 57.8317C156.187 57.8164 155.989 57.8011 155.79 57.7857V37.0654Z" fill="#139CD6"/>
              <path d="M87.1191 68.4558C76.5245 70.7307 66.2272 71.8932 55.7674 70.0862C44.1101 68.0721 38.5233 54.8174 45.5336 45.8173C48.2457 42.3377 52.1037 40.7073 56.3621 39.8019C61.7665 38.6548 67.2026 38.8121 72.6268 39.5794C73.6339 36.3184 71.5444 32.4437 68.063 31.8951C64.8474 31.3849 61.5088 31.3427 58.2416 31.4961C55.4145 31.6266 52.6152 32.3209 49.3876 32.835C48.769 29.1483 48.1505 25.4615 47.4844 21.51C49.3797 21.0573 50.9419 20.5893 52.5359 20.3169C60.2201 19.0049 67.9203 18.2107 75.561 20.5586C82.3571 22.6456 86.0723 27.3106 86.8138 34.0051C87.2975 38.3824 87.2658 42.8211 87.3174 47.2329C87.3927 53.4325 87.3451 59.6321 87.3253 65.8278C87.3253 66.6603 87.1945 67.4967 87.1151 68.4596L87.1191 68.4558ZM72.8845 49.5539C68.7014 48.4491 64.7403 48.5066 60.8546 49.7496C58.4993 50.5054 57.4287 52.3123 57.5953 54.5873C57.7816 57.1269 58.9077 58.3776 61.5841 59.0451C65.339 59.9812 69.0702 59.7894 72.8845 59.141V49.5539Z" fill="#139CD6"/>
              <path d="M97.8729 33.0265C97.2583 29.2477 96.6635 25.5955 96.0251 21.69C97.9165 21.2373 99.4787 20.7731 101.081 20.5007C108.832 19.181 116.6 18.3831 124.288 20.8077C130.906 22.8946 134.455 27.5136 135.347 34.0316C135.735 36.8782 135.878 66.8519 135.878 68.8698C131.033 69.5757 126.092 70.646 121.378 70.8494C115.882 71.0834 110.272 71.0527 104.855 70.2509C94.7484 68.7509 90.363 61.9413 91.4138 52.592C92.3099 44.6085 98.9395 41.2249 104.911 39.9934C110.319 38.877 115.751 39.0151 121.172 39.7862C122.119 36.4447 120.049 32.6352 116.564 32.0904C113.349 31.5879 110.006 31.5457 106.743 31.6991C103.916 31.8334 101.12 32.524 97.8768 33.0342L97.8729 33.0265ZM121.378 49.7493C117.183 48.6406 113.115 48.6521 109.126 50.0639C107.274 50.7199 106.112 52.0972 106.1 54.1304C106.088 55.9949 106.445 57.7596 108.42 58.6113C112.607 60.4259 116.921 59.9579 121.378 59.3748V49.7493Z" fill="#139CD6"/>
              <path d="M187.75 27.1689H200.545V70.9727H187.75V27.1689Z" fill="#139CD6"/>
              <path d="M201.995 13.6562C201.983 18.0833 198.664 21.2253 194.033 21.2023C189.513 21.1793 186.297 18.0296 186.293 13.614C186.289 9.12157 189.532 5.97574 194.164 5.98341C198.751 5.99109 202.01 9.18295 201.999 13.6562H201.995Z" fill="#F6881F"/>
              <path d="M34.5317 17.5889H0V30.4561H34.5317V17.5889Z" fill="#F6881F"/>
            </g>
            <defs>
              <clipPath id="clip0_670_468">
                <rect width="202" height="70.973" fill="white"/>
              </clipPath>
            </defs>
          </svg>
        </div>
        <div className="context">Live Stream</div>
      </header>

      <main>
        <section className="controls">
          <div className="control-group">
            <label>Vehicle live stream</label>
            <div className="static-vehicle">
              {vehicleInfo.vehicleNumber
                ? <span className="vehicle-name">{vehicleInfo.vehicleNumber}</span>
                : <span className="vehicle-name-skeleton" aria-hidden="true" />
              }
              {(() => {
                const isOffline = vehicleInfo.isLive === false;

                return isOffline ? (
                  <span className="status offline">
                    <i className="dot"></i>OFFLINE
                  </span>
                ) : (
                  <span className="status live">
                    <i className="dot"></i>LIVE
                  </span>
                );
              })()}
            </div>
          </div>

          <div className="control-group camera-group" ref={cameraMenuRef}>
            <label>Camera views</label>
            <div className="camera-select">
              <button
                className={`camera-select-trigger ${isMenuOpen ? 'open' : ''}`}
                type="button"
                onClick={() => setIsMenuOpen((prev) => !prev)}
              >
                <span className="camera-select-value">
                  {selectedCameras.length === 1
                    ? selectedCameras[0]
                    : `${selectedCameras.length} views selected`}
                </span>
                <svg className="camera-chevron" viewBox="0 0 24 24">
                  <path d="M7 10l5 5 5-5z" />
                </svg>
              </button>

              <div className={`camera-menu ${isMenuOpen ? 'show' : ''}`}>
                {availableCameras.map((cam) => {
                  const isSelected = selectedCameras.includes(cam);
                  return (
                    <button
                      key={cam}
                      type="button"
                      className="camera-option"
                      onClick={() => toggleCamera(cam)}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        readOnly
                        tabIndex={-1}
                      />
                      <span>{cam}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section className="video-section">
          <div className={getGridClass()}>
            {selectedCameras.map((cam) => {
              const state = cameraStates[cam] || 'connecting';
              const isErrorState = typeof state === 'object' && state.type === 'error';
              const secondsLeft = timers[cam] || 0;
              const timerLabel = formatTimerLabel(secondsLeft);
              const offset = 69.12 * (1 - secondsLeft / STREAM_DURATION);

              return (
                <article
                  key={cam}
                  className={`frame ${state === 'ended' || isErrorState ? 'ended' : ''}`}
                  data-camera={cam}
                >
                  <div className="feed">
                    {cameraUrls[cam] && state === 'live' && (
                      <VTVideoContainer
                        url={cameraUrls[cam]}
                        elementId={`video-player-${cam}`}
                        isEnded={false}
                        onRefresh={() => refreshStream(cam)}
                      />
                    )}
                  </div>

                  <div className="hud">
                    <div className="hud-right">
                      {state === 'live' && (
                        <div
                          className="stream-timer"
                          title={`Stream ends in ${timerLabel}`}
                        >
                          <svg viewBox="0 0 27 27" aria-hidden="true">
                            <circle className="timer-track" cx="13.5" cy="13.5" r="11" />
                            <circle
                              className="timer-progress"
                              cx="13.5"
                              cy="13.5"
                              r="11"
                              style={{ strokeDashoffset: offset }}
                            />
                          </svg>
                          <span className="timer-text">{timerLabel}</span>
                        </div>
                      )}
                      <div className="cam-name">{cam}</div>
                    </div>
                  </div>

                  {state === 'connecting' && (
                    <div className="center-state">
                      <div className="loader"></div>
                      <span>Connecting to live stream...</span>
                    </div>
                  )}

                  {isErrorState && (
                    <div className="center-state">
                      <button
                        className="restart-stream"
                        onClick={() => refreshStream(cam)}
                        title="Retry stream"
                        aria-label={`Retry ${cam}`}
                      >
                        <svg viewBox="0 0 24 24">
                          <path d="M12 5V1L6 7l6 6V9c3.31 0 6 2.69 6 6a6 6 0 0 1-6 6c-2.01 0-3.79-.99-4.88-2.51l-1.46 1.46A8 8 0 1 0 12 5z" />
                        </svg>
                      </button>
                      <strong style={{ textTransform: 'capitalize' }}>
                        {state.message || 'Device offline'}
                      </strong>
                      <span>Unable to load live video stream.</span>
                    </div>
                  )}

                  {state === 'ended' && (
                    <div className="center-state">
                      <button
                        className="restart-stream"
                        onClick={() => refreshStream(cam)}
                        title="Restart stream"
                        aria-label={`Restart ${cam}`}
                      >
                        <svg viewBox="0 0 24 24">
                          <path d="M12 5V1L6 7l6 6V9c3.31 0 6 2.69 6 6a6 6 0 0 1-6 6c-2.01 0-3.79-.99-4.88-2.51l-1.46 1.46A8 8 0 1 0 12 5z" />
                        </svg>
                      </button>
                      <strong>Stream ended</strong>
                      <span>This live stream session has ended.</span>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </section>

        <section className="info">
          <div className="info-icon">◷</div>
          <div>
            <strong>{expiryInfo.date}</strong>
          </div>
        </section>

        <section className="caution">
          <span className="caution-icon">↗</span>
          <span>
            <strong>Note:</strong> Longer live streaming uses more data and may exhaust your plan sooner. Streaming quality may also drop in low network areas.
          </span>
        </section>
      </main>

      <VTCamSwapModal
        open={swapModalOpen}
        pendingCamera={pendingCamera}
        onClose={() => setSwapModalOpen(false)}
        onAddView={handleAddView}
        onSwapView={handleSwapView}
      />
    </div>
  );
}

export default withApollo(VTLiveStream);
